import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Menu,
  ScrollArea,
  useMantineColorScheme,
} from "@mantine/core";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import NotificationEmptyState from "../NotificationEmptyState/NotificationEmptyState";
import { NotificationList } from "../NotificationList";

import { Modal } from "@/components/elements";
import { IconChevronDown } from "@/components/icons";
import {
  getNotificationCountByFilter,
  useNotificationCounts,
} from "@/services/notifications/get-notification-count";
import { useInfiniteNotifications } from "@/services/notifications/get-notifications";
import { useUpdateNotificationStatus } from "@/services/notifications/update-notification-status";

import type { Notification } from "@/types/notification";
import type { FC } from "react";

export type NotificationsModalProps = {
  opened: boolean;
  onClose: () => void;
};

const NotificationsModal: FC<NotificationsModalProps> = ({ opened, onClose }) => {
  const { t } = useTranslation();

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const selectColor = isDark ? "#a1b0e0" : "#143AB1";
  const panelBackground =
    "light-dark(var(--mantine-color-white), var(--mantine-color-primaryDark-8))";

  const { mutate: updateStatus, isPending: isUpdating } = useUpdateNotificationStatus();

  const [notificationFilter, setNotificationFilter] = useState<"all" | "delivered" | "read">("all");

  const notificationStatus = notificationFilter === "all" ? undefined : notificationFilter;
  const {
    data: response,
    isPending: isLoadingNotifications,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteNotifications({ status: notificationStatus });
  const { data: notificationCounts } = useNotificationCounts();
  const viewportRef = useRef<HTMLDivElement>(null);

  const currentNotifications = useMemo<Notification[]>(
    () => response?.pages.flatMap((page) => page.results) ?? [],
    [response?.pages]
  );

  const filteredNotifications = currentNotifications;

  const unreadNotifications = useMemo(
    () => currentNotifications.filter((notification) => notification.status === "delivered"),
    [currentNotifications]
  );

  const readNotifications = useMemo(
    () => currentNotifications.filter((notification) => notification.status === "read"),
    [currentNotifications]
  );

  const notificationCount = getNotificationCountByFilter(notificationCounts, notificationFilter, {
    total: response?.pages[0]?.total ?? currentNotifications.length,
    delivered: unreadNotifications.length,
    read: readNotifications.length,
  });

  const showEmptyState = !isLoadingNotifications && filteredNotifications.length === 0;

  const canMarkAllAsRead = (notificationCounts?.delivered ?? unreadNotifications.length) > 0;

  const FILTER_LABELS: Record<"all" | "delivered" | "read", string> = {
    all: "All",
    delivered: "Unread",
    read: "Read",
  };

  function markAllAsRead() {
    void markAllNotificationsAsRead();
  }

  async function markAllNotificationsAsRead() {
    let loadedPages = response;

    while (loadedPages?.pages.length) {
      const lastPage = loadedPages.pages[loadedPages.pages.length - 1];
      if (lastPage.page * lastPage.page_size >= lastPage.total) break;

      const nextPage = await fetchNextPage();
      loadedPages = nextPage.data;
    }

    loadedPages?.pages
      .flatMap((page) => page.results)
      .filter((notification) => notification.status === "delivered")
      .forEach((notification) => updateStatus({ id: notification.id, status: "read" }));
  }

  function handleScrollPositionChange() {
    const viewport = viewportRef.current;
    if (!viewport || !hasNextPage || isFetchingNextPage) return;

    if (viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 100) {
      void fetchNextPage();
    }
  }

  return (
    <Modal
      opened={opened}
      fullScreen
      onClose={onClose}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
      title={t("components.NotificationMenu.title")}
      styles={{
        header: {
          paddingBottom: 0,
          backgroundColor: panelBackground,
        },
        close: {
          color: "#143ab1",
          flexShrink: 0,
        },
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          backgroundColor: panelBackground,
        },
        content: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: panelBackground,
        },
        inner: {
          overflow: "hidden",
        },
      }}
    >
      <Flex
        px="lg"
        py="md"
        justify="space-between"
        align="center"
        gap="sm"
        wrap="wrap"
        style={{ flexShrink: 0 }}
      >
        <Flex align="center" gap="md" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
          <Badge
            variant="light"
            style={{
              borderRadius: "50%",
              minWidth: 24,
              height: 24,
              padding: "0 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {notificationCount}
          </Badge>

          <Menu position="bottom-start" shadow="md" withinPortal>
            <Menu.Target>
              <Group
                gap={4}
                wrap="nowrap"
                style={{ cursor: "pointer", minWidth: 0 }}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <Box component="span" c={selectColor} fz={14} fw={500}>
                  {FILTER_LABELS[notificationFilter]}
                </Box>
                <IconChevronDown size={14} color={selectColor} />
              </Group>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={() => setNotificationFilter("all")}>
                {t("components.NotificationMenu.all")}
              </Menu.Item>
              <Menu.Item onClick={() => setNotificationFilter("delivered")}>
                {t("components.NotificationMenu.unread")}
              </Menu.Item>
              <Menu.Item onClick={() => setNotificationFilter("read")}>
                {t("components.NotificationMenu.read")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>

        {canMarkAllAsRead && notificationFilter !== "read" && (
          <Button
            size="compact-xs"
            miw="fit-content"
            variant="transparent"
            onClick={markAllAsRead}
            disabled={isUpdating || isFetchingNextPage}
            style={{ flexShrink: 0 }}
          >
            {t("components.NotificationMenu.mark_all_as_read")}
          </Button>
        )}
      </Flex>

      <Box style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {showEmptyState ? (
          <Center h="100%" px="lg">
            <NotificationEmptyState type={notificationFilter} />
          </Center>
        ) : (
          <ScrollArea
            h="100%"
            type="auto"
            offsetScrollbars
            viewportRef={viewportRef}
            onScrollPositionChange={handleScrollPositionChange}
          >
            <NotificationList
              items={filteredNotifications}
              onRead={(id) => {
                const notification = currentNotifications.find((item) => item.id === id);

                if (!notification) return;

                updateStatus({
                  id,
                  status: notification.status === "delivered" ? "read" : "delivered",
                });
              }}
            />
          </ScrollArea>
        )}
      </Box>
    </Modal>
  );
};

export default NotificationsModal;
